import { forwardRef, useMemo, type ComponentProps, type KeyboardEvent, type MutableRefObject } from "react";
import type { AdToken } from "./adTokens";
import { scanAdTokens } from "./adTokenScan";
import AdTokenHighlight, { type AdTokenHighlightFieldProps } from "./AdTokenHighlight";
import AdTokenMenu from "./AdTokenMenu";
import { useAdTokenPicker, type AdTokenFieldHandle } from "./useAdTokenPicker";

/**
 * A one-line field with the ad token picker and highlights (MPN-1933) -- AdTokenTextarea's
 * counterpart for an `<input>`, as in a Create Studio text layer's Text property.
 *
 * The menu hangs under the input rather than at the caret: one line has nowhere else for the
 * caret to be, so there is nothing to measure.
 *
 * While the menu is open, the keys it uses are the menu's: the caller's onKeyDown does not see
 * them. A rename field that commits on Enter and cancels on Escape keeps doing so whenever the
 * menu is shut, and Escape with the menu open only closes the menu.
 */

type AdTokenInputProps = Omit<ComponentProps<"input">, "onChange" | "ref" | "value"> & {
    value: string;
    /** The vocabulary, already loaded. Empty means the picker never opens. */
    tokens: AdToken[];
    /** The text after an edit, whether it was typed or inserted from the menu. */
    onValueChange: (value: string) => void;
    /** The input element itself, for a caller that needs to focus or select in it. */
    inputRef?: MutableRefObject<HTMLInputElement | null>;
    /**
     * Color the tokens in place. Off for a field that only wants the picker -- a layer name
     * (MPN-1933) -- which then renders as the bare input, with nothing wrapped around it.
     */
    highlight?: boolean;
};

/** The keys an open menu with matches acts on. */
const MENU_KEYS = ["ArrowDown", "ArrowUp", "Enter", "Tab"];

const AdTokenInput = forwardRef<AdTokenFieldHandle, AdTokenInputProps>(function AdTokenInput({
    tokens,
    value,
    onValueChange,
    inputRef,
    highlight = true,
    onKeyDown,
    onBlur,
    onClick,
    onKeyUp,
    onScroll,
    onCompositionStart,
    onCompositionEnd,
    style,
    className,
    ...rest
}, ref) {
    const picker = useAdTokenPicker<HTMLInputElement>({ tokens, value, onValueChange, ref });
    const ranges = useMemo(
        () => (highlight && tokens.length ? scanAdTokens(value, tokens) : []),
        [highlight, value, tokens],
    );

    const menuKeys = (e: KeyboardEvent<HTMLInputElement>) =>
        picker.open && (e.key === "Escape" || (picker.matches.length > 0 && MENU_KEYS.includes(e.key)));

    const input = (field?: AdTokenHighlightFieldProps) => (
        <input
            {...rest}
            ref={(node) => {
                picker.fieldRef.current = node;
                field?.ref(node);
                if (inputRef) inputRef.current = node;
            }}
            value={value}
            className={field ? field.className : className}
            style={field ? field.style : style}
            onScroll={(e) => {
                onScroll?.(e);
                field?.onScroll(e);
            }}
            onCompositionStart={(e) => {
                onCompositionStart?.(e);
                field?.onCompositionStart(e);
            }}
            onCompositionEnd={(e) => {
                onCompositionEnd?.(e);
                field?.onCompositionEnd(e);
            }}
            {...picker.fieldProps({
                onKeyDown: (e) => {
                    if (!menuKeys(e)) onKeyDown?.(e);
                },
                onKeyUp,
                onClick,
                onBlur,
            })}
        />
    );

    return (
        <>
            {highlight ? (
                <AdTokenHighlight value={value} ranges={ranges} multiline={false} className={className} style={style}>
                    {input}
                </AdTokenHighlight>
            ) : (
                input()
            )}
            {picker.open && (
                <AdTokenMenu
                    anchorEl={picker.fieldRef.current}
                    value={value}
                    context={picker.context}
                    matches={picker.matches}
                    active={picker.active}
                    onActive={picker.setActive}
                    onInsert={picker.insert}
                />
            )}
        </>
    );
});

export default AdTokenInput;
